if (!customElements.get('gift-card-balance-form')) {
  customElements.define(
    'gift-card-balance-form',
    class GiftCardBalanceForm extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('form');
        this.input = this.querySelector('input[name="code"]');
        this.error = this.querySelector('.gift-card-balance__error');
        this.result = this.querySelector('.gift-card-balance__result');
        this.resultAmount = this.querySelector('.gift-card-balance__result-amount');
        this.button = this.querySelector('button[type="submit"]');
        this.buttonLabel = this.button?.querySelector('span');

        this.config = {
          token: this.dataset.storefrontToken,
          shopDomain: this.dataset.shopDomain,
          apiVersion: this.dataset.apiVersion || '2024-10',
          cartStorageKey: 'gift-card-balance-check-cart-id',
        };

        if (!this.form || !this.input) return;

        this.form.addEventListener('submit', this.onSubmit.bind(this));
        this.input.addEventListener('input', this.clearMessages.bind(this));
      }

      async onSubmit(event) {
        event.preventDefault();

        const code = this.input.value.replace(/\s/g, '').trim();
        if (!code) {
          this.showError(window.giftCardBalanceStrings?.emptyError || 'Please enter your gift card number.');
          this.input.focus();
          return;
        }

        if (!this.config.token) {
          this.showError(window.giftCardBalanceStrings?.configError || 'Gift card balance checking is not configured.');
          return;
        }

        this.setLoading(true);
        this.clearMessages();

        try {
          const balance = await this.checkBalance(code);
          this.showResult(balance);
        } catch (error) {
          this.showError(error.message || window.giftCardBalanceStrings?.invalidError || 'Could not find a gift card with that number.');
        } finally {
          this.setLoading(false);
        }
      }

      async storefrontFetch(query, variables = {}) {
        const response = await fetch(
          `https://${this.config.shopDomain}/api/${this.config.apiVersion}/graphql.json`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Shopify-Storefront-Access-Token': this.config.token,
            },
            body: JSON.stringify({ query, variables }),
          }
        );

        const payload = await response.json();

        if (!response.ok || payload.errors?.length) {
          throw new Error(payload.errors?.[0]?.message || window.giftCardBalanceStrings?.requestError || 'Unable to check gift card balance.');
        }

        return payload.data;
      }

      async createCartId() {
        const data = await this.storefrontFetch(`
          mutation giftCardBalanceCartCreate {
            cartCreate(input: {}) {
              cart { id }
              userErrors { message }
            }
          }
        `);

        const userErrors = data.cartCreate?.userErrors || [];
        if (userErrors.length) {
          throw new Error(userErrors[0].message);
        }

        const cartId = data.cartCreate?.cart?.id;
        if (!cartId) {
          throw new Error(window.giftCardBalanceStrings?.requestError || 'Unable to check gift card balance.');
        }

        sessionStorage.setItem(this.config.cartStorageKey, cartId);
        return cartId;
      }

      async getOrCreateCartId() {
        const storedCartId = sessionStorage.getItem(this.config.cartStorageKey);
        return storedCartId || this.createCartId();
      }

      async updateGiftCardCodes(cartId, giftCardCodes) {
        const data = await this.storefrontFetch(
          `
            mutation giftCardBalanceCheck($cartId: ID!, $giftCardCodes: [String!]!) {
              cartGiftCardCodesUpdate(cartId: $cartId, giftCardCodes: $giftCardCodes) {
                cart {
                  appliedGiftCards {
                    balance {
                      amount
                      currencyCode
                    }
                    lastCharacters
                  }
                }
                userErrors {
                  message
                  code
                }
              }
            }
          `,
          { cartId, giftCardCodes }
        );

        return data.cartGiftCardCodesUpdate;
      }

      async checkBalance(code) {
        const cartId = await this.getOrCreateCartId();
        const payload = await this.updateGiftCardCodes(cartId, [code]);

        if (payload.userErrors?.length) {
          throw new Error(payload.userErrors[0].message);
        }

        const appliedGiftCard = payload.cart?.appliedGiftCards?.[0];
        if (!appliedGiftCard) {
          throw new Error(window.giftCardBalanceStrings?.invalidError || 'Could not find a gift card with that number.');
        }

        await this.updateGiftCardCodes(cartId, []);

        return appliedGiftCard.balance;
      }

      formatBalance(balance) {
        const amount = Number(balance.amount);
        const locale = document.documentElement.lang || 'en-AU';

        try {
          return new Intl.NumberFormat(locale, {
            style: 'currency',
            currency: balance.currencyCode,
          }).format(amount);
        } catch (error) {
          return `${balance.currencyCode} ${amount.toFixed(2)}`;
        }
      }

      showResult(balance) {
        if (!this.result || !this.resultAmount) return;
        this.resultAmount.textContent = this.formatBalance(balance);
        this.result.classList.remove('hidden');
      }

      showError(message) {
        if (!this.error) return;
        this.error.textContent = message;
        this.error.classList.remove('hidden');
        this.result?.classList.add('hidden');
      }

      clearMessages() {
        if (this.error) {
          this.error.textContent = '';
          this.error.classList.add('hidden');
        }
        this.result?.classList.add('hidden');
      }

      setLoading(isLoading) {
        if (!this.button) return;
        this.button.disabled = isLoading;
        this.button.classList.toggle('loading', isLoading);
        if (this.buttonLabel) {
          this.buttonLabel.textContent = isLoading
            ? (window.giftCardBalanceStrings?.loading || 'Checking...')
            : (window.giftCardBalanceStrings?.submit || 'Check balance');
        }
      }
    }
  );
}
